import { h, Fragment } from 'preact';
import { useState, useRef, useEffect, useMemo, useLayoutEffect } from 'preact/hooks';
import Panel from 'utils/panel';
import TrapFocus from 'utils/trap-focus';
import { formatMoney, formatCartLinesAjax } from 'utils/storefront/format-resource';
import CloseIcon from 'utils/icon/icon-close.svg';
import { a11yLabel } from 'utils/a11y-label';
import { withCartContext } from './cart-context';
import CartItem from './cart-item';
import FreeShipping from './shipping-bar';
import { resolveShippingThreshold } from './shipping-threshold';
import CartRecommendations from './cart-recommendations';
import DonationsProduct from './donations-product';
import CartGwpThreshold from './cart-gwp-threshold';
import { setGiftCandidateState, getGiftCandidateState } from '../utils/gift-candidate-state';
import CartService from './cart-service';
import PromoCode, {
  getStoredPromoCode,
  clearStoredPromoCode,
  getCartDiscountCode,
  PROMO_REMOVED_FLAG,
} from './promo-code';

const FREE_GIFT_ATTRIBUTE_KEY = '_free_gift_item';
const FREE_GIFT_CANDIDATE_KEY = '_giftCandidate';

const getLineProperties = (line = {}) => {
  if (line.properties && Object.keys(line.properties).length > 0) return line.properties;
  if (Array.isArray(line.attributes)) {
    return line.attributes.reduce((acc, attr) => {
      const key = attr?.key || attr?.name;
      if (key) acc[key] = attr?.value;
      return acc;
    }, {});
  }
  if (line.merchandise?.attributes && Array.isArray(line.merchandise.attributes)) {
    return line.merchandise.attributes.reduce((acc, attr) => {
      const key = attr?.key || attr?.name;
      if (key) acc[key] = attr?.value;
      return acc;
    }, {});
  }
  return {};
};

const lineHasFreeGiftFlag = (line) => {
  const props = getLineProperties(line);
  const giftFlag = props[FREE_GIFT_ATTRIBUTE_KEY];
  const candidateFlag = props[FREE_GIFT_CANDIDATE_KEY];
  const hasGift = typeof giftFlag === 'string' ? giftFlag.toLowerCase() === 'true' : giftFlag === true;
  const hasCandidate =
    typeof candidateFlag === 'string' ? candidateFlag.toLowerCase() === 'true' : candidateFlag === true;
  return hasGift || hasCandidate;
};

const getAmountInCentsFromMoney = (money) => {
  if (!money) return 0;
  const { amount } = money;
  if (typeof amount === 'number') return Math.round(amount * 100);
  if (typeof amount === 'string') return Math.round(parseFloat(amount) * 100);
  return 0;
};

const isPromotionActive = (startDate, endDate) => {
  const now = new Date();

  if (startDate) {
    const start = new Date(startDate);
    // Validate that the date was parsed correctly
    if (Number.isNaN(start.getTime())) {
      // console.warn('Invalid start date:', startDate);
      return false;
    }
    // Use the exact datetime from metafield, preserving the time component
    // Shopify stores datetimes in UTC (e.g., "2025-11-17T05:00:00Z")
    // JavaScript Date comparison handles timezone conversion automatically
    if (now < start) return false;
  }

  if (endDate) {
    const end = new Date(endDate);
    // Validate that the date was parsed correctly
    if (Number.isNaN(end.getTime())) {
      // console.warn('Invalid end date:', endDate);
      return false;
    }
    // Use the exact datetime from metafield, preserving the time component
    // Shopify stores datetimes in UTC (e.g., "2025-11-17T15:30:00Z")
    // JavaScript Date comparison handles timezone conversion automatically
    if (now > end) return false;
  }

  return true;
};

const isGiftCardLine = (line) => {
  if (!line) return false;
  const productType =
    (
      line.product_type ||
      line.productType ||
      line.merchandise?.product?.productType ||
      line.merchandise?.product?.product_type ||
      ''
    )
      ?.toString()
      .toLowerCase?.() || '';
  if (productType.includes('gift card')) return true;

  const title =
    (line.product_title || line.title || line.merchandise?.product?.title || '')?.toString().toLowerCase?.() || '';
  if (title.includes('gift card')) return true;

  const giftCardFlag =
    line?.merchandise?.product?.giftCard ||
    line?.merchandise?.product?.isGiftCard ||
    line?.properties?._is_gift_card === 'true' ||
    (Array.isArray(line.attributes) &&
      line.attributes.some(
        (attr) => (attr?.key === '_is_gift_card' || attr?.name === '_is_gift_card') && attr?.value === 'true',
      ));

  return Boolean(giftCardFlag);
};

const hasPromoExcludeTag = (line) => {
  try {
    if (!line) return false;

    // First, check cart line properties/attributes for stored promo_exclude flag (most reliable for AJAX cart)
    const lineProps = getLineProperties(line);
    const promoExcludeFlag = lineProps._promo_exclude;
    if (promoExcludeFlag) {
      if (typeof promoExcludeFlag === 'string') {
        if (promoExcludeFlag.toLowerCase() === 'true') {
          return true;
        }
      } else if (promoExcludeFlag === true) {
        return true;
      }
    }

    // Check product tags from Storefront API (line.merchandise.product.tags)
    const storefrontTags = line.merchandise?.product?.tags;
    if (Array.isArray(storefrontTags) && storefrontTags.includes('promo_exclude')) {
      return true;
    }

    // Check product tags from AJAX cart (line.product?.tags or line.tags)
    const ajaxTags = line.product?.tags || line.tags;
    if (Array.isArray(ajaxTags) && ajaxTags.includes('promo_exclude')) {
      return true;
    }

    return false;
  } catch (error) {
    // If any error occurs, safely return false to not break cart functionality
    // console.warn('Error checking promo_exclude tag:', error);
    return false;
  }
};

const getLineSubtotalCents = (line) => {
  if (!line) return 0;
  if (line.cost?.totalAmount) return getAmountInCentsFromMoney(line.cost.totalAmount);
  if (line.cost?.subtotalAmount) return getAmountInCentsFromMoney(line.cost.subtotalAmount);
  if (typeof line.line_price === 'number') return line.line_price;
  if (typeof line.final_line_price === 'number') return line.final_line_price;
  if (typeof line.discounted_price === 'number' && typeof line.quantity === 'number') {
    return line.discounted_price * line.quantity;
  }
  if (typeof line.price === 'number' && typeof line.quantity === 'number') {
    return line.price * line.quantity;
  }
  return 0;
};

const getCartLinesForProgress = (cart) => {
  if (!cart) return [];
  if (cart.mode === 'storefront') {
    if (Array.isArray(cart.state?.lines)) {
      return cart.state.lines;
    }
    return [];
  }
  if (Array.isArray(cart.state?.items)) {
    return cart.state.items;
  }
  if (Array.isArray(cart.state?.lines)) return cart.state.lines;
  if (Array.isArray(cart.items)) return cart.items;
  return [];
};

const STORAGE_KEY = 'freeGiftSettings';

const persistGiftSettings = (threshold, startDate, endDate) => {
  if (typeof window === 'undefined') return;
  try {
    const payload = {
      threshold: Number(threshold || 0),
      startDate,
      endDate,
      updatedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    // console.warn('Failed to persist gift settings', error);
  }
};

const loadGiftSettings = () => {
  if (typeof window === 'undefined') return { threshold: 0, startDate: null, endDate: null };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { threshold: 0, startDate: null, endDate: null };
    const parsed = JSON.parse(raw);
    return {
      threshold: Number(parsed?.threshold || 0),
      startDate: parsed?.startDate || null,
      endDate: parsed?.endDate || null,
    };
  } catch (error) {
    // console.warn('Failed to parse stored gift settings', error);
    return { threshold: 0, startDate: null, endDate: null };
  }
};

const FreeGiftProgress = ({
  cart,
  threshold,
  startDate,
  endDate,
  giftAmountCents = 0,
  freeGiftsCollectionUrl = '/collections/free-gifts',
  qualifiedText,
  spendMoreText,
  selectButtonText,
  testMode = false,
  customer = { isLoggedIn: false, hasTestTag: false },
}) => {
  // Use translations with fallback to props (from settings) or default values
  const qualifiedTextValue =
    qualifiedText || window.TMW?.Strings?.cart?.freeGiftQualified || 'You qualify for a free gift!';
  const spendMoreTextValue =
    spendMoreText || window.TMW?.Strings?.cart?.freeGiftSpendMore || 'Spend {amount} more to unlock your free gift!';
  const selectButtonTextValue =
    selectButtonText || window.TMW?.Strings?.cart?.freeGiftSelectButton || 'Select your Gift';
  let normalizedThreshold = Number(threshold || 0);
  let normalizedStartDate = startDate;
  let normalizedEndDate = endDate;

  if ((!normalizedThreshold || Number.isNaN(normalizedThreshold)) && typeof window !== 'undefined') {
    const cached = loadGiftSettings();
    normalizedThreshold = cached.threshold;
    normalizedStartDate = cached.startDate;
    normalizedEndDate = cached.endDate;
  } else if (typeof window !== 'undefined') {
    persistGiftSettings(normalizedThreshold, normalizedStartDate, normalizedEndDate);
  }

  // Calculate a hash of cart line quantities and prices to detect any changes
  const cartHash = useMemo(() => {
    if (!cart) return '';
    const cartLines = getCartLinesForProgress(cart);
    if (!cartLines.length) return 'empty';
    return cartLines.map((line) => `${line.key || line.id}:${line.quantity}:${getLineSubtotalCents(line)}`).join('|');
  }, [cart?.state?.items, cart?.state?.lines, cart?.state?.item_count]);

  // Simple logic: if threshold reached → _giftCandidate = true, else remove from storage
  useLayoutEffect(() => {
    // Check if promotion is active (dates valid)
    if (!isPromotionActive(normalizedStartDate, normalizedEndDate)) {
      setGiftCandidateState(false);
      return;
    }

    // Check if we have valid threshold
    if (!normalizedThreshold || Number.isNaN(normalizedThreshold) || normalizedThreshold <= 0) {
      setGiftCandidateState(false);
      return;
    }

    // Check if cart exists and has items
    if (!cart) {
      setGiftCandidateState(false);
      return;
    }

    const cartLines = getCartLinesForProgress(cart);
    if (!cartLines.length) {
      setGiftCandidateState(false);
      return;
    }

    // Calculate cart subtotal (excluding gift cards, free gift items, and products with promo_exclude tag)
    const subtotalCents = cartLines
      .filter((line) => !isGiftCardLine(line) && !hasPromoExcludeTag(line))
      .reduce((acc, line) => acc + getLineSubtotalCents(line), 0);

    const adjustedSubtotalCents = Math.max(0, subtotalCents - giftAmountCents);
    const thresholdCents = Math.round(normalizedThreshold * 100);

    // Simple check: if threshold reached → true, else false
    const meetsThreshold = adjustedSubtotalCents >= thresholdCents;
    setGiftCandidateState(meetsThreshold);
  }, [cartHash, normalizedThreshold, normalizedStartDate, normalizedEndDate, giftAmountCents]);

  // Free gift logic only shows if minimum spending amount exists and current date is between start and end date
  if (!cart || !normalizedThreshold || Number.isNaN(normalizedThreshold) || normalizedThreshold <= 0) {
    return null;
  }
  if (!isPromotionActive(normalizedStartDate, normalizedEndDate)) {
    return null;
  }

  // Test mode logic: if testMode is true, only show to logged-in customers with "test" tag
  // Check this early before doing any calculations
  // testMode is read dynamically from collection metafield via Liquid template
  if (testMode) {
    const isLoggedIn = customer && (customer.isLoggedIn === true || customer.isLoggedIn === 'true');
    const hasTestTag = customer && (customer.hasTestTag === true || customer.hasTestTag === 'true');
    if (!isLoggedIn || !hasTestTag) {
      return null;
    }
  }

  const cartLines = getCartLinesForProgress(cart);
  if (!cartLines.length) {
    return null;
  }

  // Hide progress bar if free gift items already exist in cart
  // Use useMemo to make it reactive to cart changes (cartHash updates when cart changes)
  const hasFreeGiftInCart = useMemo(() => {
    const lines = getCartLinesForProgress(cart);
    if (!lines || !lines.length) return false;
    return lines.some((line) => lineHasFreeGiftFlag(line));
  }, [cartHash, cart]);

  const subtotalCents = cartLines
    .filter((line) => !isGiftCardLine(line) && !hasPromoExcludeTag(line))
    .reduce((acc, line) => acc + getLineSubtotalCents(line), 0);

  const adjustedSubtotalCents = Math.max(0, subtotalCents - giftAmountCents);

  const thresholdCents = Math.round(normalizedThreshold * 100);
  if (thresholdCents <= 0) {
    return null;
  }

  const remainingCents = Math.max(0, thresholdCents - adjustedSubtotalCents);
  const progressPercent = Math.min(100, (adjustedSubtotalCents / thresholdCents) * 100);
  const qualified = remainingCents <= 0;

  const currencyCode = window.TMW?.localization?.currencyCode;
  const formattedSubtotal = formatMoney({
    amount: adjustedSubtotalCents / 100,
    currencyCode,
  });

  const formattedRemaining = formatMoney({
    amount: remainingCents / 100,
    currencyCode,
  });

  const title = qualified ? qualifiedTextValue : spendMoreTextValue.replace('{amount}', formattedRemaining);

  // Handler to remove existing free gift items before navigating to collection
  const handleSelectGiftClick = async (e) => {
    e.preventDefault();

    if (!cart) return;

    // Get cart lines
    const allCartLines = cart?.mode === 'storefront' ? cart?.state?.lines : formatCartLinesAjax(cart?.state?.items);

    if (!Array.isArray(allCartLines) || !allCartLines.length) {
      // No items, just navigate
      window.location.href = freeGiftsCollectionUrl;
      return;
    }

    // Find all free gift items
    const freeGiftLines = allCartLines.filter((line) => lineHasFreeGiftFlag(line));

    if (freeGiftLines.length > 0) {
      // Get line keys/ids for removal
      const lineKeysToRemove = freeGiftLines.map((line) => line.key || line.id).filter(Boolean);

      if (lineKeysToRemove.length > 0) {
        // Remove free gift items
        await CartService.remove(lineKeysToRemove);
        // Update gift candidate state after removal
        setGiftCandidateState(getGiftCandidateState());
      }
    }

    // Navigate to free gifts collection
    window.location.href = freeGiftsCollectionUrl;
  };

  const safeProgress = Math.max(0, Math.min(100, progressPercent || 0));

  return (
    <div
      className="progress_block border-b border-grey-5 px-15 py-20 lg:px-20"
      style={hasFreeGiftInCart ? { display: 'none' } : {}}
    >
      <div className="rounded-[8px] bg-grey-6 px-15 py-16 lg:px-20">
        <p className="body-14-bold text-primary">{title}</p>
        <p className="body-12-light mt-6 text-faded-black">
          Subtotal (excludes gift cards): <span className="body-12-med text-primary">{formattedSubtotal}</span>
        </p>
        <div className="relative mt-16 h-4 w-full overflow-hidden rounded-full bg-grey-4">
          <div
            style={{ width: `${safeProgress}%` }}
            className="absolute left-0 top-0 h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
          ></div>
          <div className="absolute inset-0 rounded-full border border-grey-5/60"></div>
        </div>
        {qualified && (
          <a
            href={freeGiftsCollectionUrl}
            onClick={handleSelectGiftClick}
            className="cta-blue mt-12 inline-block rounded px-20 py-6 text-12 no-underline"
          >
            {selectButtonTextValue}
          </a>
        )}
      </div>
    </div>
  );
};

// Renders Nosto placement via DOM API so Preact never clears injected campaign content on re-render
const NostoPlacement = ({ id, wrapperProps }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (!ref.current || ref.current.querySelector('.nosto_element')) return;
    const el = document.createElement('div');
    el.className = 'nosto_element';
    if (id) el.id = id;
    ref.current.appendChild(el);
  }, [id]);
  return h('div', { ref, ...(wrapperProps || {}) });
};

const Cart = (props) => {
  const { cart } = props;
  const closeTriggerEl = useRef(null);
  const isCartRedirect = new URLSearchParams(window.location.search).get('cart');
  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: document.querySelector('[data-header-cart-trigger]'),
    closeTriggerEl: null,
  });
  const [isOpen, setIsOpen] = useState(isCartRedirect);
  const [giftCandidateActive, setGiftCandidateActive] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('_giftCandidate') === 'true';
  });
  const removalInProgressRef = useRef(false);
  const prevGiftCandidateRef = useRef(giftCandidateActive);

  const lines = cart?.mode === 'storefront' ? cart?.state?.lines : formatCartLinesAjax(cart?.state?.items);
  const giftAmountCents = Array.isArray(lines)
    ? lines.filter((line) => lineHasFreeGiftFlag(line)).reduce((acc, line) => acc + getLineSubtotalCents(line), 0)
    : 0;

  // Sort lines to put free gift items first
  const sortedLines = useMemo(() => {
    if (!Array.isArray(lines) || !lines.length) return lines;
    return [...lines].sort((a, b) => {
      const aIsFreeGift = lineHasFreeGiftFlag(a);
      const bIsFreeGift = lineHasFreeGiftFlag(b);
      // Free gift items come first
      if (aIsFreeGift && !bIsFreeGift) return -1;
      if (!aIsFreeGift && bIsFreeGift) return 1;
      // Maintain original order for items of the same type
      return 0;
    });
  }, [lines]);

  useEffect(() => {
    if (!closeTriggerEl.current) return;
    setPanelTriggers((value) => ({
      ...value,
      closeTriggerEl: closeTriggerEl.current,
    }));
  }, [closeTriggerEl]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateCandidate = () => {
      setGiftCandidateActive(window.localStorage.getItem('_giftCandidate') === 'true');
    };

    updateCandidate();
    window.addEventListener('storage', updateCandidate);

    return () => {
      window.removeEventListener('storage', updateCandidate);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setGiftCandidateActive(window.localStorage.getItem('_giftCandidate') === 'true');
    }
  }, [cart.state?.item_count, cart.state?.items, cart.state?.lines]);

  useEffect(() => {
    const previous = prevGiftCandidateRef.current;
    prevGiftCandidateRef.current = giftCandidateActive;

    // Don't remove if gift candidate is still active
    if (giftCandidateActive) return;
    // Don't remove if state hasn't changed
    if (previous === giftCandidateActive) return;
    // Don't remove if no lines exist
    if (!Array.isArray(lines) || !lines.length) return;
    // Don't remove if removal is already in progress
    if (removalInProgressRef.current) return;

    // Check if user actually qualifies before removing
    // Calculate cart subtotal excluding gift items and products with promo_exclude tag
    const cartLines = getCartLinesForProgress(cart);
    const subtotalCents = cartLines
      .filter((line) => !isGiftCardLine(line) && !lineHasFreeGiftFlag(line) && !hasPromoExcludeTag(line))
      .reduce((acc, line) => acc + getLineSubtotalCents(line), 0);

    // Get free gift threshold from settings
    const freeGiftThreshold = Number(props.data?.freeGiftSettings?.minimumSpendingAmount || 0);
    const freeGiftStartDate = props.data?.freeGiftSettings?.startDate || null;
    const freeGiftEndDate = props.data?.freeGiftSettings?.endDate || null;

    // Check if promotion is active (date range)
    const isDateRangeValid = isPromotionActive(freeGiftStartDate, freeGiftEndDate);

    // Only remove gift items if:
    // 1. Threshold exists and user doesn't meet it, OR
    // 2. Date range is invalid
    // This prevents removing items when localStorage is cleared on unrelated pages
    const shouldRemoveGifts =
      freeGiftThreshold > 0 && (subtotalCents < Math.round(freeGiftThreshold * 100) || !isDateRangeValid);

    if (!shouldRemoveGifts) {
      // User still qualifies, restore state
      if (subtotalCents >= Math.round(freeGiftThreshold * 100) && isDateRangeValid) {
        setGiftCandidateState(true);
        setGiftCandidateActive(true);
      }
      return;
    }

    const updatePayload = lines
      .filter((line) => lineHasFreeGiftFlag(line))
      .map((line) => {
        if (line?.key) return { key: line.key, quantity: 0 };
        if (line?.id) return { id: line.id, quantity: 0 };
        return null;
      })
      .filter(Boolean);

    if (!updatePayload.length || !cart?.update) return;

    removalInProgressRef.current = true;

    (async () => {
      try {
        await cart.update(updatePayload);
      } catch (error) {
        // console.warn('Failed to update cart when removing gift lines', error);
      } finally {
        removalInProgressRef.current = false;
      }
    })();
  }, [giftCandidateActive, lines, cart, props.data?.freeGiftSettings]);

  const hasDonationProduct = useMemo(
    () => cart.state.items?.some((item) => item.product_type === 'Donation'),
    [cart.state],
  );

  // Detect cart lines that are customizable products (SKU prefix `C00`) but are
  // missing the design properties (`_isCustomProduct=true` / `_customProductRecipeID__c`).
  // This usually happens when a customer uses Shopify's native "Buy again" button.
  // The Cart and Checkout Validation function blocks checkout server-side; here we
  // disable the storefront Checkout button so the customer is told before they try.
  const hasBrokenCustomLine = useMemo(() => {
    if (!Array.isArray(lines)) return false;
    return lines.some((l) => {
      const sku = l?.sku || l?.merchandise?.sku || '';
      if (!sku.startsWith('C00')) return false;
      const lineProps = l?.properties || {};
      const hasFlag = lineProps._isCustomProduct === 'true';
      const hasRecipe = (lineProps._customProductRecipeID__c || '').toString().length > 0;
      return !(hasFlag || hasRecipe);
    });
  }, [lines]);

  let trapFocus = null;

  useEffect(() => {
    if (isOpen) {
      trapFocus = new TrapFocus(document.querySelector('[data-minicart]'));
      document.body.classList.add('scroll-lock', 'has-minicart');
      trapFocus.init();

      const kustomerIframe = document.querySelector('#kustomer-ui-sdk-iframe');
      if (kustomerIframe && !kustomerIframe.classList.contains('kustomer-chat-window-left')) {
        kustomerIframe.classList.add('kustomer-chat-window-left');
      }
    } else {
      const kustomerIframe = document.querySelector('#kustomer-ui-sdk-iframe');
      if (kustomerIframe && kustomerIframe.classList.contains('kustomer-chat-window-left')) {
        kustomerIframe.classList.remove('kustomer-chat-window-left');
      }
    }

    return () => {
      document.body.classList.remove('scroll-lock', 'has-minicart');
      if (trapFocus) trapFocus.destroy(document.querySelector('[data-header-cart-trigger]'));
    };
  }, [isOpen, trapFocus]);

  const cartRecommendations = useMemo(
    () => (
      <CartRecommendations
        title={props.data.recTitle}
        placementId={props.data.placementId}
        layout={props.data.layout}
      />
    ),
    [props.data.recTitle, props.data.placementId, props.data.layout],
  );

  const EmptyCartContent = () => (
    <div>
      <div className="flex flex-col items-center gap-15 py-40 lg:py-[50px]">
        <h4 className="text-faded-black">{window.TMW.Strings.cart.empty}</h4>
        {props.data.emptyButtonLink && props.data.emptyButtonText && (
          <a
            className="cta-blue no-underline"
            href={props.data.emptyButtonLink}
          >
            {props.data.emptyButtonText}
          </a>
        )}
      </div>

      {props.data.placementId && <div className="border-t border-grey-5">{cartRecommendations}</div>}
    </div>
  );

  const activeGwpDiscounts = useMemo(() => {
    const now = new Date();
    return props.data.gwp.filter((gwp) => new Date(gwp.startsAt) < now && (!gwp.endsAt || new Date(gwp.endsAt) > now));
  }, []);

  // Shoplift A/B test: behavior is determined by the active test key
  const { shopliftTestKey } = props.data;

  // Shipping threshold test: variant_b swaps the mini-cart goal to the Variant B threshold.
  // The arm-to-number decision lives in `shipping-threshold.js` so it can be unit-tested without
  // mounting the cart — see that file for why returning 0 here is never correct.
  const [shippingThreshold, setShippingThreshold] = useState(() =>
    resolveShippingThreshold({
      enabled: props.data.shopliftTestEnabled,
      testKey: shopliftTestKey,
      variantKey: window.TMW?.shopliftTests?.shipping_threshold?.variantKey,
      controlThreshold: props.data.freeShippingThreshold,
      variantBThreshold: props.data.freeShippingThresholdVariantB,
    }),
  );

  // Mini cart promo test: variant_b shows promo code field (variant_a = control, no change)
  const [shopliftPromoEnabled, setShopliftPromoEnabled] = useState(() => {
    if (props.data.shopliftTestEnabled && shopliftTestKey === 'mini_cart_promo') {
      const test = window.TMW?.shopliftTests?.mini_cart_promo;
      return test && test.variantKey === 'variant_b';
    }
    return false;
  });

  useEffect(() => {
    if (!props.data.shopliftTestEnabled) return undefined;

    const handleShopliftResolved = (e) => {
      const { testKey, variantKey } = e.detail || {};

      if (testKey === 'shipping_threshold' && variantKey === 'variant_b') {
        setShippingThreshold(
          resolveShippingThreshold({
            enabled: props.data.shopliftTestEnabled,
            testKey,
            variantKey,
            controlThreshold: props.data.freeShippingThreshold,
            variantBThreshold: props.data.freeShippingThresholdVariantB,
          }),
        );
      }

      if (testKey === 'mini_cart_promo' && variantKey === 'variant_b') {
        setShopliftPromoEnabled(true);
      }
    };

    document.addEventListener('shoplift:test-resolved', handleShopliftResolved);
    return () => document.removeEventListener('shoplift:test-resolved', handleShopliftResolved);
  }, [props.data.shopliftTestEnabled]);

  // Resolve promo code visibility: Shoplift test overrides section setting when active
  const showPromoCode =
    props.data.shopliftTestEnabled && shopliftTestKey === 'mini_cart_promo'
      ? shopliftPromoEnabled
      : props.data.enablePromoCode;

  const showShippingBar = cart.state.item_count > 0 && shippingThreshold && shippingThreshold !== 0;
  const freeGiftThreshold = Number(props.data?.freeGiftSettings?.minimumSpendingAmount || 0);
  const freeGiftStartDate = props.data?.freeGiftSettings?.startDate || null;
  const freeGiftEndDate = props.data?.freeGiftSettings?.endDate || null;
  const freeGiftsCollectionUrl = '/collections/free-gifts';
  // Read testMode dynamically from collection metafield (passed via Liquid template)
  const testMode = Boolean(
    props.data?.freeGiftSettings?.testMode === true ||
      props.data?.freeGiftSettings?.testMode === 'true' ||
      props.data?.freeGiftSettings?.testMode === 1,
  );
  const customer = props.data?.freeGiftSettings?.customer || { isLoggedIn: false, hasTestTag: false };

  const getCheckoutUrl = () => {
    const userPromoCode = getStoredPromoCode();
    if (userPromoCode) {
      sessionStorage.removeItem(PROMO_REMOVED_FLAG);
      return `/discount/${encodeURIComponent(userPromoCode)}?redirect=/checkout`;
    }
    const storedPromo = JSON.parse(localStorage.getItem('promo'));
    if (storedPromo) {
      return `/discount/${storedPromo.discount_code}?redirect=/checkout`;
    }
    return '/checkout';
  };

  const handleCheckoutClick = (e) => {
    if (hasBrokenCustomLine) {
      e.preventDefault();
      return;
    }
    if (sessionStorage.getItem(PROMO_REMOVED_FLAG)) {
      e.preventDefault();
      sessionStorage.removeItem(PROMO_REMOVED_FLAG);
      // Re-clear the discount from the cart right before navigating to checkout
      // to ensure no stale session discount is carried over
      fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' }),
      }).then(() => {
        window.location.href = '/checkout';
      });
    }
  };

  return (
    <Panel
      triggers={panelTriggers}
      defaultOpen={isCartRedirect}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      context="cart"
      showBackdrop={true}
      additionalClasses="w-full max-w-[420px] cart-panel"
    >
      <div
        className="relative flex h-full flex-col"
        data-minicart
      >
        <button
          className="absolute right-15 top-20 text-faded-black lg:right-20"
          ref={closeTriggerEl}
          aria-label={a11yLabel('cart.close')}
        >
          <CloseIcon />
        </button>
        <h4 className="border-b border-grey-5 px-15 py-20 text-faded-black lg:px-20">
          {window.TMW.Strings.cart.title}
          {cart.state.item_count > 0 && <span className="inline-block pl-4">({cart.state.item_count})</span>}
        </h4>
        {props.data.placementMessageId && props.data.useDomaineMarkup && (
          <NostoPlacement id={props.data.placementMessageId} />
        )}
        {!props.data.useDomaineMarkup && <NostoPlacement wrapperProps={{ id: 'custom_nosto_element' }} />}
        {!!showShippingBar && (
          <FreeShipping
            threshold={shippingThreshold}
            cart={cart}
          />
        )}
        {cart.state.item_count > 0 && freeGiftThreshold > 0 && (
          <FreeGiftProgress
            cart={cart}
            threshold={freeGiftThreshold}
            startDate={freeGiftStartDate}
            endDate={freeGiftEndDate}
            giftAmountCents={giftAmountCents}
            freeGiftsCollectionUrl={freeGiftsCollectionUrl}
            testMode={testMode}
            customer={customer}
          />
        )}
        {cart.state.item_count > 0 && !!activeGwpDiscounts.length && (
          <div className="flex flex-col gap-20 p-20">
            {activeGwpDiscounts.map((gwp) => (
              <CartGwpThreshold
                key={gwp.id}
                gwp={gwp}
              />
            ))}
          </div>
        )}
        {cart.state.item_count === 0 ? (
          <EmptyCartContent />
        ) : (
          <div className="flex h-auto grow flex-col overflow-y-auto border-t border-grey-5">
            {sortedLines.map((line, index) => (
              <CartItem
                key={line.id}
                line={line}
                index={index}
              />
            ))}
            {!hasDonationProduct && props.data.donationProduct?.variants?.length > 0 && (
              <DonationsProduct donationProduct={props.data.donationProduct} />
            )}
            {props.data.placementId && <div id={props.data.placementId}></div>}
          </div>
        )}
        {cart.state.item_count > 0 && (
          <div className="border-t border-grey-5 px-15 py-20 lg:px-20">
            {(() => {
              // Use actual cart state as source of truth for active discount
              const activePromoCode = getCartDiscountCode(cart.state);

              // Sync localStorage to match cart state (for getCheckoutUrl)
              if (activePromoCode && getStoredPromoCode() !== activePromoCode) {
                try {
                  localStorage.setItem('cartPromoCode', activePromoCode);
                } catch (error) {
                  // console.warn('Failed to store promo code in localStorage', error);
                }
              } else if (!activePromoCode && getStoredPromoCode()) {
                clearStoredPromoCode();
              }

              const merchandisePrice = activePromoCode
                ? cart.state.original_total_price || cart.state.total_price || 0
                : cart.state.total_price || 0;
              return (
                <>
                  <div className="mb-12 flex items-center justify-between">
                    <h4 className="body-14">{window.TMW.Strings.cart.merchandise}</h4>
                    <span className="body-14">
                      <span className="body-14">
                        {cart.mode === 'storefront'
                          ? formatMoney({
                              amount: Math.max(
                                0,
                                (activePromoCode
                                  ? cart.state.original_total_price || cart?.state?.subtotal?.amount || 0
                                  : cart?.state?.subtotal?.amount || 0) -
                                  giftAmountCents / 100,
                              ),
                            })
                          : formatMoney(
                              {
                                amount: Math.max(0, merchandisePrice - giftAmountCents),
                              },
                              true,
                            )}
                      </span>
                    </span>
                  </div>
                  <div className="mb-12 flex items-center justify-between">
                    <h4
                      className="body-14"
                      dangerouslySetInnerHTML={{ __html: window.TMW.Strings.cart.shippingAndTaxes }}
                    ></h4>
                    <span className="body-14">{window.TMW.Strings.cart.calculatedAtCheckout}</span>
                  </div>
                  {showPromoCode && <PromoCode />}
                  {activePromoCode && cart.state.total_discount > 0 && (
                    <div className="mb-12 flex items-center justify-between">
                      <h4 className="body-14">{window.TMW.Strings.cart.discount || 'Discount'}</h4>
                      <span className="body-14 text-error">
                        -
                        {formatMoney(
                          cart.mode === 'storefront'
                            ? { amount: cart.state.total_discount / 100 }
                            : { amount: cart.state.total_discount },
                          cart.mode !== 'storefront',
                        )}
                      </span>
                    </div>
                  )}
                </>
              );
            })()}
            <div className="mb-12 flex items-center justify-between">
              <h4 className="body-14">{window.TMW.Strings.cart.subtotal}</h4>
              <span className="">
                {cart.mode === 'storefront'
                  ? formatMoney({
                      amount: Math.max(0, (cart?.state?.subtotal?.amount || 0) - giftAmountCents / 100),
                    })
                  : formatMoney(
                      cart?.state?.total_price
                        ? { amount: Math.max(0, cart.state.total_price - giftAmountCents) }
                        : { amount: 0 },
                      true,
                    )}
              </span>
            </div>
            <a
              id="checkout"
              className={`cta-blue h-45 w-full justify-center rounded py-15 no-underline${
                hasBrokenCustomLine ? ' pointer-events-none cursor-not-allowed opacity-50' : ''
              }`}
              href={hasBrokenCustomLine ? '#' : getCheckoutUrl()}
              aria-disabled={hasBrokenCustomLine ? 'true' : undefined}
              tabIndex={hasBrokenCustomLine ? -1 : undefined}
              onClick={handleCheckoutClick}
            >
              {cart.mode === 'storefront' ? cart?.state?.checkoutUrl : window.TMW.Strings.cart.checkout}
            </a>
          </div>
        )}
      </div>
    </Panel>
  );
};

export default withCartContext(Cart);
